import React, {FC} from "react";
import {
    serviceCardCardTitle,
    serviceCardCardWrapper,
    serviceCardIconClass,
    serviceCardIconWrapper,
    serviceCardTitle,
} from "@/styles/variants";
import {cn} from "@/lib";
import {IServiceCard} from "@/interfaces";
import Link from "next/link";
import {useTranslations} from "next-intl";
import {BdAltArrowLeft} from "solar-icon-react/bd";

const VARIANT_ICON_COLOR: Record<string, string> = {
  "emerald-mint": "text-emerald-mint!",
  "sky-bliss": "text-sky-bliss!",
  "teal-ocean": "text-teal-ocean!",
  "sunset-gold": "text-sunset-gold!",
  "yellow-500": "text-yellow-500!",
  "rose-500": "text-rose-500!",
};

interface ServiceCardProps extends IServiceCard {}

const ServiceCard: FC<ServiceCardProps> = ({
  category,
  services,
  variant,
  categoryIcon,
}) => {
  const t = useTranslations("categories");
  if (services.length === 0) {
    return null;
  }
  return (
    <div className={serviceCardCardWrapper({ variant })}>
      <div className="shadow-main flex h-full flex-col rounded-2xl bg-white">
        {/* Category Header */}
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div className="flex items-center">
            {categoryIcon && (
              <span className="shrink-0">
                {React.cloneElement(categoryIcon, {
                  size: 22,
                  className: VARIANT_ICON_COLOR[variant],
                })}
              </span>
            )}
            <h2 className={serviceCardCardTitle({ variant })}>
              {t(category as any)}
            </h2>
          </div>
          {category === "billsServices" && (
            <Link
              href="/services/bills"
              className={cn(
                "flex shrink-0 items-center gap-2 rounded-md bg-rose-500/10 px-2 py-1 text-sm font-medium text-rose-500",
              )}
            >
              <span className="text-xs">مشاهده همه</span>
              <BdAltArrowLeft />
            </Link>
          )}
        </div>

        {/* Services List */}
        <ul className="flex flex-col gap-0.5 p-2 pb-4">
          {services.map((service, idx) => (
            <li key={idx} className="relative">
              <Link
                id={`service-card-${service.flag}`}
                href={
                  service.disabled !== "unavailability" ? service.href : "#"
                }
                target="_blank"
                rel="noopener"
                title={service.label}
                aria-disabled={service.disabled === "unavailability"}
                className={cn(
                  "group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors",
                  service.disabled === "unavailability"
                    ? "pointer-events-none opacity-40"
                    : "hover:bg-gray-50",
                )}
              >
                {/* Icon */}
                <div className={serviceCardIconWrapper({ variant })}>
                  {React.cloneElement(service.icon, {
                    className: cn(
                      serviceCardIconClass({ variant }),
                      service.disabled === "unavailability" && "text-gray-400!",
                    ),
                  })}
                </div>

                {/* Label */}
                <span className={serviceCardTitle({ variant })}>
                  {service.label}
                </span>

                {/* Tag */}
                {service.tag && service.disabled !== "unavailability" && (
                  <span
                    className={cn(
                      "shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold text-white",
                      service.tag === "new" ? "bg-red-500" : "bg-primary-main",
                    )}
                  >
                    {service.tag === "new" ? "جدید" : "به زودی"}
                  </span>
                )}

                {/* Arrow */}
                {service.disabled !== "unavailability" && (
                  <BdAltArrowLeft
                    size={14}
                    className="shrink-0 text-gray-300 transition-colors group-hover:text-gray-500"
                  />
                )}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ServiceCard;
